/**
 * Custom hooks per Folders
 * Include CRUD operations, sharing, document association
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
} from '@/lib/api-client';
import type {
  FolderResponse,
  FolderDetailResponse,
  FolderCreateRequest,
  FolderUpdateRequest,
  FolderMoveRequest,
  FolderShareRequest,
  DocumentAssociationRequest,
  FolderListFilters,
  FolderDocumentResponse,
  UnmappedDocumentResponse,
  AssociateDocumentsResponse,
  ShareFolderResponse,
  GlobalSearchResponse,
} from '@/types/api/folders';

export const FOLDERS_QUERY_KEYS = {
  all: ['folders'] as const,
  lists: () => [...FOLDERS_QUERY_KEYS.all, 'list'] as const,
  list: (filters?: FolderListFilters) => [...FOLDERS_QUERY_KEYS.lists(), filters] as const,
  details: () => [...FOLDERS_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...FOLDERS_QUERY_KEYS.details(), id] as const,
  documents: (id: string) => [...FOLDERS_QUERY_KEYS.detail(id), 'documents'] as const,
  unmapped: () => [...FOLDERS_QUERY_KEYS.all, 'unmapped'] as const,
  containerFolder: (containerId: string) => [...FOLDERS_QUERY_KEYS.all, 'container', containerId] as const,
  search: (query: string) => [...FOLDERS_QUERY_KEYS.all, 'search', query] as const,
};

/**
 * Hook per lista folders con filtri
 */
export function useFolders(filters?: FolderListFilters) {
  return useQuery({
    queryKey: FOLDERS_QUERY_KEYS.list(filters),
    queryFn: async (): Promise<FolderResponse[]> => {
      return await apiGet<FolderResponse[]>('/folders', {
        params: filters as Record<string, string | number | boolean | null | undefined> | undefined,
      });
    },
  });
}

/**
 * Hook per dettaglio folder
 */
export function useFolder(folderId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: FOLDERS_QUERY_KEYS.detail(folderId),
    queryFn: async (): Promise<FolderDetailResponse> => {
      return await apiGet<FolderDetailResponse>(`/folders/${folderId}`);
    },
    enabled: options?.enabled !== false && !!folderId,
  });
}

/**
 * Hook per creare custom folder
 */
export function useCreateFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: FolderCreateRequest): Promise<FolderResponse> => {
      return await apiPost<FolderResponse>('/folders', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.lists() });
    },
  });
}

/**
 * Hook per aggiornare folder
 */
export function useUpdateFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ folderId, data }: { folderId: string; data: FolderUpdateRequest }): Promise<FolderResponse> => {
      return await apiPut<FolderResponse>(`/folders/${folderId}`, data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.detail(variables.folderId) });
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.lists() });
    },
  });
}

/**
 * Hook per eliminare folder
 */
export function useDeleteFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (folderId: string): Promise<void> => {
      return await apiDelete<void>(`/folders/${folderId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook per spostare folder
 */
export function useMoveFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ folderId, data }: { folderId: string; data: FolderMoveRequest }): Promise<FolderResponse> => {
      return await apiPost<FolderResponse>(`/folders/${folderId}/move`, data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.detail(variables.folderId) });
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.lists() });
    },
  });
}

/**
 * Hook per condividere folder
 */
export function useShareFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ folderId, data }: { folderId: string; data: FolderShareRequest }): Promise<ShareFolderResponse[]> => {
      return await apiPost<ShareFolderResponse[]>(`/folders/${folderId}/share`, data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.detail(variables.folderId) });
    },
  });
}

/**
 * Hook per rimuovere condivisione folder
 */
export function useRemoveFolderSharing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ folderId, companyId }: { folderId: string; companyId: string }): Promise<void> => {
      return await apiDelete<void>(`/folders/${folderId}/share/${companyId}`);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.detail(variables.folderId) });
    },
  });
}

/**
 * Hook per lista documenti in folder
 */
export function useFolderDocuments(folderId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: FOLDERS_QUERY_KEYS.documents(folderId),
    queryFn: async (): Promise<FolderDocumentResponse[]> => {
      return await apiGet<FolderDocumentResponse[]>(`/folders/${folderId}/documents`);
    },
    enabled: options?.enabled !== false && !!folderId,
  });
}

/**
 * Hook per associare documenti a folder (bulk)
 */
export function useAssociateDocumentsToFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ folderId, data }: { folderId: string; data: DocumentAssociationRequest }): Promise<AssociateDocumentsResponse> => {
      return await apiPost<AssociateDocumentsResponse>(`/folders/${folderId}/documents`, data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.documents(variables.folderId) });
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.detail(variables.folderId) });
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.unmapped() });
    },
  });
}

/**
 * Hook per rimuovere documento da folder
 */
export function useRemoveDocumentFromFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ folderId, documentId }: { folderId: string; documentId: string }): Promise<void> => {
      return await apiDelete<void>(`/folders/${folderId}/documents/${documentId}`);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.documents(variables.folderId) });
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.detail(variables.folderId) });
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.unmapped() });
    },
  });
}

/**
 * Hook per get o crea container folder
 */
export function useGetOrCreateContainerFolder(containerId: string) {
  return useQuery({
    queryKey: FOLDERS_QUERY_KEYS.containerFolder(containerId),
    queryFn: async (): Promise<FolderResponse> => {
      return await apiGet<FolderResponse>(`/folders/containers/${containerId}`);
    },
    enabled: !!containerId,
  });
}

/**
 * Hook per lista documenti unmapped
 */
export function useUnmappedDocuments() {
  return useQuery({
    queryKey: FOLDERS_QUERY_KEYS.unmapped(),
    queryFn: async (): Promise<UnmappedDocumentResponse[]> => {
      return await apiGet<UnmappedDocumentResponse[]>('/folders/documents/unmapped');
    },
  });
}

/**
 * Hook per ricerca globale (folders, documenti, containers)
 */
export function useGlobalSearch(query: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: FOLDERS_QUERY_KEYS.search(query),
    queryFn: async (): Promise<GlobalSearchResponse> => {
      return await apiGet<GlobalSearchResponse>('/folders/search', {
        params: { q: query, limit: 50 },
      });
    },
    enabled: options?.enabled !== false && !!query && query.trim().length > 0,
  });
}

